import React, { useState } from 'react';
type Length = 60 | 75 | 90;

/** The 90-Minute Practice Formula, exactly as timed on page 4 of every volume. */
const FORMULA: Record<Length, {
  warm: number;
  drills: number;
  main: number;
  strength: number;
  cool: number;
}> = {
  90: {
    warm: 15,
    drills: 15,
    main: 40,
    strength: 10,
    cool: 10
  },
  75: {
    warm: 12,
    drills: 12,
    main: 33,
    strength: 8,
    cool: 10
  },
  60: {
    warm: 10,
    drills: 10,
    main: 27,
    strength: 5,
    cool: 8
  }
};
const COLORS = {
  warm: '#C8452B',
  drills: '#E89B0C',
  main: '#2563A8',
  strength: '#2E7D4F',
  cool: '#14213D'
};
const C = 120;
const R_OUT = 112;
const R_IN = 68;
const GAP = 1.4;
const pt = (r: number, deg: number): [number, number] => [C + r * Math.sin(deg * Math.PI / 180), C - r * Math.cos(deg * Math.PI / 180)];
function segment(a0: number, a1: number) {
  const s = a0 + GAP / 2;
  const e = a1 - GAP / 2;
  const large = e - s > 180 ? 1 : 0;
  const [x0, y0] = pt(R_OUT, s);
  const [x1, y1] = pt(R_OUT, e);
  const [x2, y2] = pt(R_IN, e);
  const [x3, y3] = pt(R_IN, s);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${R_OUT} ${R_OUT} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} L${x2.toFixed(2)} ${y2.toFixed(2)} A${R_IN} ${R_IN} 0 ${large} 0 ${x3.toFixed(2)} ${y3.toFixed(2)} Z`;
}
export function PracticeFormula() {
  const [length, setLength] = useState<Length>(90);
  const t = FORMULA[length];
  const parts = [{
    key: 'warm',
    min: t.warm,
    color: COLORS.warm
  }, {
    key: 'drills',
    min: t.drills,
    color: COLORS.drills
  }, {
    key: 'main',
    min: t.main,
    color: COLORS.main
  }, {
    key: 'strength',
    min: t.strength,
    color: COLORS.strength
  }, {
    key: 'cool',
    min: t.cool,
    color: COLORS.cool
  }];
  let angle = 0;
  const arcs = parts.map(p => {
    const sweep = p.min / length * 360;
    const a0 = angle;
    angle += sweep;
    const mid = a0 + sweep / 2;
    return {
      ...p,
      d: segment(a0, a0 + sweep),
      label: pt((R_OUT + R_IN) / 2, mid)
    };
  });
  const legend = [{
    name: 'Arrival + Warm-Up',
    min: `${t.warm} min`,
    color: COLORS.warm,
    note: 'Starts as athletes arrive. The same team warm-up, so nobody waits for instructions.'
  }, {
    name: 'Drills',
    min: `${t.drills} min`,
    color: COLORS.drills,
    note: 'Running form: A-skips, B-skips, high knees, with one cue per mistake.'
  }, {
    name: 'Main Set by event group',
    min: `${t.main} min`,
    color: COLORS.main,
    note: 'Every group works from its own Fence Card. You coach where the risk is.'
  }, {
    name: 'Strength / Mobility',
    min: `${t.strength} min`,
    color: COLORS.strength,
    note: 'Bodyweight and core circuits. No weight room needed.'
  }, {
    name: 'Cool-Down + Card Check-Out',
    min: `${t.cool} min`,
    color: COLORS.cool,
    note: 'Initials on the card, equipment back, one question: “What did we work on today?”'
  }];
  return <div className="flex h-full flex-col rounded-3xl bg-chalk-light p-5 text-left text-ink shadow-page sm:p-7" data-id="element-274" data-mp="21bxe0y31wrot">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-275" data-mp="puxhtrdl1spi">
        <div data-id="element-276" data-mp="psumcrtnv7de">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-clay" data-id="element-277" data-mp="ibejmdvrpswc">Time</p>
          <h3 className="font-display text-xl font-bold uppercase leading-tight sm:text-2xl" data-id="element-278" data-mp="id97o12c614n">The 90-Minute Practice Formula</h3>
        </div>
        <div className="flex rounded-full bg-chalk-dark/70 p-1" role="group" aria-label="Practice length" data-id="element-279" data-mp="rccnmw67i0ex">
          {([60, 75, 90] as Length[]).map(l => <button key={l} type="button" onClick={() => setLength(l)} aria-pressed={length === l} className={`min-h-[44px] min-w-[52px] rounded-full px-3 text-sm font-bold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-clay ${length === l ? 'bg-ink text-chalk' : 'text-ink-soft hover:text-ink'}`} data-id="element-280" data-mp="3ust8q1vlowhs">
              {l}′
            </button>)}
        </div>
      </div>

      <div className="mt-5 grid items-center gap-6 sm:grid-cols-[minmax(0,240px)_1fr]" data-id="element-281" data-mp="3740kysp4tuy9">
        <svg viewBox="0 0 240 240" className="mx-auto w-full max-w-[240px]" role="img" aria-label={`A ${length}-minute practice: warm-up ${t.warm}, drills ${t.drills}, main set ${t.main}, strength ${t.strength}, cool-down ${t.cool} minutes`} data-id="element-282" data-mp="3kqhfsgofelag">
          {arcs.map(a => <g key={a.key} data-id="element-283" data-mp="3a9ed3l1lwqf">
              <path d={a.d} fill={a.color} data-id="element-284" data-mp="3bkgz02r4yfy" />
              <text x={a.label[0]} y={a.label[1] + 4.5} textAnchor="middle" fill="#FFFFFF" fontFamily="Oswald, 'Arial Narrow', sans-serif" fontWeight="700" fontSize="13" data-id="element-285" data-mp="7bkvsltrjbnj">
                {a.min}′
              </text>
            </g>)}
          <text x="120" y="128" textAnchor="middle" fill="#14213D" fontFamily="Oswald, 'Arial Narrow', sans-serif" fontWeight="700" fontSize="52" data-id="element-286" data-mp="m5zesmdbul27">
            {length}
          </text>
          <text x="120" y="150" textAnchor="middle" fill="#4B5672" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="13" letterSpacing="1.5" data-id="element-287" data-mp="2o1t2tyrkho7w">
            MIN
          </text>
        </svg>

        <ol className="grid gap-2.5" data-id="element-288" data-mp="10brcgz3fvj3n">
          {legend.map(l => <li key={l.name} className="flex gap-3" data-id="element-289" data-mp="wd23rjtcipqc">
              <span className="mt-1 h-3 w-3 flex-none rounded-[3px]" style={{
            background: l.color
          }} aria-hidden="true" data-id="element-290" data-mp="rdfpd71s73fy" />
              <div data-id="element-291" data-mp="1o3q44tn73r2y">
                <p className="text-sm font-bold leading-snug" data-id="element-292" data-mp="1o259z54sma5m">
                  {l.name} <span className="font-semibold text-ink-soft" data-id="element-293" data-mp="1k1lb9x4kmyhd">· {l.min}</span>
                </p>
                <p className="text-[13px] leading-snug text-ink-soft" data-id="element-294" data-mp="1f1yy1noqti8k">{l.note}</p>
              </div>
            </li>)}
        </ol>
      </div>
    </div>;
}